import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../../hooks/useChat';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const AIChatAssistant = ({ professional }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastResponseRef = useRef('');

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', true);

  useEffect(() => {
    if (error) toast?.error('Chat unavailable. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && response !== lastResponseRef?.current) {
      lastResponseRef.current = response;
      setMessages(prev => {
        const withoutStreaming = prev?.filter(m => m?.role !== 'streaming');
        if (isLoading) {
          return [...withoutStreaming, { role: 'streaming', content: response }];
        }
        return [...withoutStreaming, { role: 'assistant', content: response }];
      });
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Hi! I'm the AI assistant for **${professional?.name}**. I can answer questions about their services, pricing, availability, experience, and qualifications. What would you like to know?`,
      }]);
    }
  }, [isOpen, professional?.name]);

  const buildSystemPrompt = () => {
    const servicesText = professional?.services
      ?.map(s => `- ${s?.name}: ${s?.price} — ${s?.description}`)
      ?.join('\n') || 'No services listed';

    const reviewsText = professional?.reviews
      ?.slice(0, 3)
      ?.map(r => `"${r?.text}" — ${r?.author} (${r?.rating}/5, ${r?.date})`)
      ?.join('\n') || 'No reviews yet';

    return `You are a helpful AI assistant representing ${professional?.name}, a ${professional?.categoryLabel} at ${professional?.company}.

Professional Profile:
- Name: ${professional?.name}
- Category: ${professional?.categoryLabel}
- Company: ${professional?.company}
- Location: ${professional?.location}
- Rating: ${professional?.rating}/5 (${professional?.reviewCount} reviews)
- Verified: ${professional?.verified ? 'Yes' : 'No'}
- Response Time: ${professional?.responseTime}
- Availability: ${professional?.availability}
- Completed Jobs: ${professional?.completedJobs}
- Repeat Clients: ${professional?.repeatClients}
- Starting Price: ${professional?.startingPrice}
- Specializations: ${professional?.specializations?.join(', ')}
- Certifications: ${professional?.certifications?.join(', ')}

Services Offered:
${servicesText}

Recent Client Reviews:
${reviewsText}

Bio: ${professional?.bio}

Contact: ${professional?.phone} | ${professional?.email}

Instructions:
- Answer questions about this professional's services, pricing, availability, experience, and qualifications
- Be helpful, professional, and concise
- If asked about booking, encourage them to use the "Book Consultation" button
- If asked something you don't know, say you'll need to check with the professional directly
- Keep responses under 150 words unless detail is needed
- Use markdown formatting for lists and emphasis`;
  };

  const handleSend = () => {
    if (!input?.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input?.trim() };
    const updatedMessages = [...messages?.filter(m => m?.role !== 'streaming'), userMessage];
    setMessages(updatedMessages);
    setInput('');
    lastResponseRef.current = '';

    const apiMessages = [
      { role: 'system', content: buildSystemPrompt() },
      ...updatedMessages?.filter(m => m?.role === 'user' || m?.role === 'assistant')?.map(m => ({ role: m?.role, content: m?.content })),
    ];

    sendMessage(apiMessages, { max_completion_tokens: 400 });
  };

  const suggestedQuestions = [
    'What are your pricing packages?',
    'How quickly do you respond?',
    'Are you available this week?',
    'What makes you different?',
  ];

  const renderMessage = (msg, idx) => {
    const isUser = msg?.role === 'user';
    const isStreaming = msg?.role === 'streaming';
    const content = msg?.content || '';

    const formattedContent = content?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')?.replace(/\*(.*?)\*/g, '<em>$1</em>')?.replace(/^- (.+)$/gm, '• $1')?.replace(/\n/g, '<br />');

    return (
      <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
        {!isUser && (
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mr-2 mt-0.5"
            style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(123,47,255,0.2))' }}
          >
            <Icon name="Sparkles" size={13} color="#00D4FF" />
          </div>
        )}
        <div
          className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
          style={{
            background: isUser
              ? 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(0,153,204,0.2))'
              : 'rgba(255,255,255,0.06)',
            border: isUser
              ? '1px solid rgba(0,212,255,0.3)'
              : '1px solid rgba(255,255,255,0.08)',
            color: isUser ? '#E2E8F0' : '#A0B4C8',
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: formattedContent }} />
          {isStreaming && (
            <span
              className="inline-block w-1.5 h-4 ml-1 animate-pulse rounded-sm"
              style={{ background: '#00D4FF', verticalAlign: 'middle' }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,212,255,0.15)' }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 transition-all duration-200"
        style={{
          background: isOpen
            ? 'linear-gradient(135deg, rgba(0,212,255,0.1), rgba(123,47,255,0.1))'
            : 'transparent',
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(123,47,255,0.2))' }}
          >
            <Icon name="MessageSquare" size={17} color="#00D4FF" />
          </div>
          <div className="text-left">
            <p className="text-sm font-bold text-white">Ask AI About {professional?.name?.split(' ')?.[0]}</p>
            <p className="text-xs" style={{ color: '#6B9EC4' }}>Instant answers about services, pricing & availability</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: '#00FF88' }}
          />
          <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={18} color="#6B9EC4" />
        </div>
      </button>
      {isOpen && (
        <div style={{ borderTop: '1px solid rgba(0,212,255,0.1)' }}>
          <div
            className="px-4 py-4 overflow-y-auto"
            style={{ height: '280px', scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,212,255,0.2) transparent' }}
          >
            {messages?.map((msg, idx) => renderMessage(msg, idx))}
            {isLoading && messages?.[messages?.length - 1]?.role !== 'streaming' && (
              <div className="flex justify-start mb-3">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mr-2"
                  style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(123,47,255,0.2))' }}
                >
                  <Icon name="Sparkles" size={13} color="#00D4FF" />
                </div>
                <div
                  className="px-4 py-3 rounded-2xl flex items-center gap-1"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  {[0, 1, 2]?.map(i => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full animate-bounce"
                      style={{ background: '#00D4FF', animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {messages?.length <= 1 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2">
              {suggestedQuestions?.map((q, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInput(q);
                    inputRef?.current?.focus();
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    background: 'rgba(0,212,255,0.08)',
                    border: '1px solid rgba(0,212,255,0.15)',
                    color: '#A0B4C8',
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <div
            className="px-4 py-3 flex gap-2"
            style={{ borderTop: '1px solid rgba(0,212,255,0.08)' }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e?.target?.value)}
              onKeyDown={(e) => e?.key === 'Enter' && handleSend()}
              placeholder={`Ask about ${professional?.name?.split(' ')?.[0]}'s services...`}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(0,212,255,0.2)',
                color: '#E2E8F0',
              }}
              disabled={isLoading}
            />
            <button
              onClick={handleSend}
              disabled={!input?.trim() || isLoading}
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200"
              style={{
                background:
                  !input?.trim() || isLoading
                    ? 'rgba(0,212,255,0.1)'
                    : 'linear-gradient(135deg, #00D4FF, #0099CC)',
                cursor: !input?.trim() || isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              <Icon name="Send" size={15} color={!input?.trim() || isLoading ? '#6B9EC4' : '#000'} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIChatAssistant;
